import classnames from 'classnames';
import moment from 'moment';
import { useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { RevisionSource } from '../../../../../typings/enums';
import { compactRevision } from '../../../../helpers/revisions';
import useProjectRevisions from '../../../../hooks/api/revisions/useProjectRevisions';
import useRevisionHistory from '../../../../hooks/api/revisions/useRevisionHistory';
import Badge from '../../../generic/Badge/Badge';
import Loader from '../../../generic/Loader';
import { Menu, MenuDivider, MenuItem, SearchMenuItem } from '../../../generic/Menu/index';
import type { BranchAndTagMap } from '../../../../hooks/api/revisions/useProjectRevisions';
import type { Revision } from '../../../../types';

import CommitStatusIcon from './CommitStatusIcon';
import { RevisionDisplayAsButton } from './RevisionDisplay';
import SourceDropdown from './SourceDropdown';
import { getRevisionMessage, getSourceText } from './utils';
import type { RevisionDropdownProps } from './types';

import styles from './Dropdown.module.less';

type ReferenceType = keyof BranchAndTagMap;

/**
 * Check if a revision should show "Analyzed:" before its commit
 * message. Since CLI revisions always have a timestamp message,
 * we add a "Analyzed:" prefix to clarify the revision value.
 */
const shouldShowAnalyzedLabel = (revision?: Revision) => {
  return revision?.source === RevisionSource.CLI;
};

const ByRevisionDropdownContent: VoidFunctionComponent<{
  project: RevisionDropdownProps['project'];
  selectedRevision: RevisionDropdownProps['selectedRevision'];
  onSelectRevision: RevisionDropdownProps['onSelectRevision'];
  searchText: string;
  source: RevisionSource | null;
  startingRevision: string;
  shouldFetchOnlyResolvedRevisions: RevisionDropdownProps['shouldFetchOnlyResolvedRevisions'];
  shouldFetchOnlyRevisionsWithSnippetAnalysis: RevisionDropdownProps['shouldFetchOnlyRevisionsWithSnippetAnalysis'];
}> = ({
  project,
  selectedRevision,
  onSelectRevision,
  source,
  searchText,
  startingRevision,
  shouldFetchOnlyResolvedRevisions,
  shouldFetchOnlyRevisionsWithSnippetAnalysis,
}) => {
  const { data, isLoading, error } = useRevisionHistory({
    revisionLocator: startingRevision,
    source,
    revisionLocatorFilter: searchText,
    shouldFetchOnlyResolvedRevisions,
    shouldFetchOnlyRevisionsWithSnippetAnalysis,
  });

  // We want to drop the first revision because it's going to be the same revision we're currently on.
  if (data?.revisions[0]?.locator === startingRevision) {
    data?.revisions.shift();
  }

  const revisions = data?.revisions || [];

  return (
    <DropdownContent
      project={project}
      selectedRevision={selectedRevision}
      onSelectRevision={onSelectRevision}
      revisions={revisions}
      isLoading={isLoading}
      error={error}
    />
  );
};

const ByReferenceDropdownContent: VoidFunctionComponent<{
  project: RevisionDropdownProps['project'];
  reference: RevisionDropdownProps['reference'];
  referenceType: RevisionDropdownProps['referenceType'];
  selectedRevision?: RevisionDropdownProps['selectedRevision'];
  searchText: string;
  source: RevisionSource | null;
  onSelectRevision: RevisionDropdownProps['onSelectRevision'];
  shouldFetchOnlyResolvedRevisions: RevisionDropdownProps['shouldFetchOnlyResolvedRevisions'];
}> = ({
  project,
  reference,
  referenceType,
  selectedRevision,
  searchText,
  source,
  onSelectRevision,
  shouldFetchOnlyResolvedRevisions,
}) => {
  const { data, isLoading, error } = useProjectRevisions({
    projectLocator: project?.data?.locator,
    refsType: referenceType,
    refName: reference,
    revisionLocatorFilter: searchText,
    source: source || undefined,
    shouldFetchOnlyResolvedRevisions,
  });

  let revisions: Revision[] = [];
  const isReferenceInfoAvailable = referenceType && reference;
  if (data && isReferenceInfoAvailable) {
    const references = data[referenceType as ReferenceType] || {};
    revisions = references[reference] || [];
  }

  return (
    <DropdownContent
      project={project}
      selectedRevision={selectedRevision}
      onSelectRevision={onSelectRevision}
      revisions={revisions}
      isLoading={isLoading}
      error={error}
    />
  );
};

/**
 * DropdownContent is the content of the dropdown menu.
 * It displays the list of revisions for the selected reference.
 * Clicking on a revision navigates to the revision page.
 */
const DropdownContent: VoidFunctionComponent<{
  project: RevisionDropdownProps['project'];
  isLoading: boolean;
  error: Error | null;
  selectedRevision?: RevisionDropdownProps['selectedRevision'];
  onSelectRevision: RevisionDropdownProps['onSelectRevision'];
  revisions: Revision[];
}> = ({ project, isLoading, error, revisions, selectedRevision, onSelectRevision }) => {
  const isSBOMProject = project.data.type === 'sbom';

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return <div className={styles.error}>Something went wrong. Please try again later.</div>;
  }

  return (
    <>
      {revisions.map((revision: Revision) => {
        const revisionMessage = getRevisionMessage({
          revision,
          isSelected: false,
          shouldShowAnalyzedLabel: shouldShowAnalyzedLabel(revision),
        });
        const label = compactRevision(revision.locator);
        const sourceText = getSourceText(revision.source);
        const isSelected = selectedRevision?.locator === revision.locator;
        let menuItem = (
          <>
            {label && (
              <Badge color="lighter-gray">
                <CommitStatusIcon revision={revision} />
                {label}
              </Badge>
            )}
            {revisionMessage}
            {sourceText && <Badge color="light-gray">{sourceText}</Badge>}
          </>
        );
        if (isSBOMProject) {
          const revisionTime = moment(revision.revision_timestamp).format('MMM. D YYYY, h:mm a Z');
          menuItem = (
            <>
              <span>{label}</span>
              {revisionTime && (
                <Badge color="light-gray">
                  <CommitStatusIcon revision={revision} />
                  {revisionTime}
                </Badge>
              )}
            </>
          );
        }

        return (
          <MenuItem
            key={revision.locator}
            className={classnames(styles.menuItem, {
              [styles.selectedMenuItem]: isSelected,
              [styles.sbomMenuItem]: isSBOMProject,
            })}
            onClick={() => {
              onSelectRevision(revision);
            }}
          >
            {menuItem}
          </MenuItem>
        );
      })}
    </>
  );
};

/**
 * Dropdown is the dropdown menu that displays the selected revision
 * and the list of revisions for the selected reference when expanded.
 */
const Dropdown: VoidFunctionComponent<RevisionDropdownProps> = ({
  selectedRevision,
  project,
  reference,
  referenceType,
  onSelectRevision,
  placeholder,
  variant = 'default',
  startingRevision,
  shouldFetchOnlyResolvedRevisions,
  shouldFetchOnlyRevisionsWithSnippetAnalysis,
}) => {
  const [searchText, setSearchText] = useState('');
  const [selectedSource, setSelectedSource] = useState<RevisionSource | null>(null);

  const isSBOMProject = project.data.type === 'sbom';
  const isHybridProject = project.data.type === 'provided' && project.data.vcs_host !== null;

  return (
    <Menu
      direction="bottom"
      menuButton={
        <RevisionDisplayAsButton
          type={isSBOMProject ? 'sbom' : 'non-sbom'}
          selectedRevision={selectedRevision}
          placeholder={placeholder}
          variant={variant}
        />
      }
    >
      <div className={styles.actions}>
        <SearchMenuItem
          placeholder="Search"
          onChange={(e) => {
            setSearchText(e.target.value);
          }}
          hasBorder
        />
        {isHybridProject && <SourceDropdown project={project} source={selectedSource} onClick={setSelectedSource} />}
      </div>
      <MenuDivider />
      {startingRevision ? (
        <ByRevisionDropdownContent
          project={project}
          selectedRevision={selectedRevision}
          onSelectRevision={onSelectRevision}
          searchText={searchText}
          source={selectedSource}
          startingRevision={startingRevision}
          shouldFetchOnlyResolvedRevisions={shouldFetchOnlyResolvedRevisions}
          shouldFetchOnlyRevisionsWithSnippetAnalysis={shouldFetchOnlyRevisionsWithSnippetAnalysis}
        />
      ) : (
        <ByReferenceDropdownContent
          project={project}
          reference={reference}
          referenceType={referenceType}
          selectedRevision={selectedRevision}
          searchText={searchText}
          source={selectedSource}
          onSelectRevision={onSelectRevision}
          shouldFetchOnlyResolvedRevisions={shouldFetchOnlyResolvedRevisions}
        />
      )}
    </Menu>
  );
};

export default Dropdown;
